import { useState, useEffect, useRef } from "react";
import { C } from "./constants";

const STEPS = [
  { id:"welcome",    tab:"overview",                   title:"Welcome to Barakah Budget 🌙",    body:"This is a demo for Ahmed Al-Farsi — a Muslim professional in Canada. Let's take a quick tour of the key features.",                                                                              anchor:null,                    arrowDir:null   },
  { id:"spending",   tab:"overview",                   title:"Monthly spending 💸",              body:"This card shows how much you've spent this month. Tap ➕ to add a new transaction instantly.",                                                                                                     anchor:"tour-spending",         arrowDir:"up"   },
  { id:"summary",    tab:"overview",                   title:"Income · Expenses · Saved 📊",     body:"These 3 strips give you an instant snapshot. Green means you saved money this month.",                                                                                                            anchor:"tour-summary",          arrowDir:"up"   },
  { id:"calendar",   tab:"calendar",                   title:"Daily calendar 📅",                body:"Tap any day to see or add transactions. Coloured dots show spending activity per day.",                                                                                                           anchor:"tour-calendar",         arrowDir:"up"   },
  { id:"recurring",  tab:"tools",   toolsTab:"recurring", title:"Recurring reminders 🔔 · Pro",           body:"The yellow banner below the calendar shows payments due this month — rent, Netflix, insurance. Tap ✓ Paid to confirm or Skip to dismiss.",                                                        anchor:null,                    arrowDir:null   },
  { id:"goals",      tab:"tools",  toolsTab:"goals",   title:"Savings goals 🎯 · Pro",           body:"Track up to 3 goals for free — Hajj, emergency fund, wedding. Upgrade to Pro for unlimited goals with AI milestone coaching.",                                                                             anchor:null,                    arrowDir:null   },
  { id:"debts",      tab:"tools",  toolsTab:"debts",   title:"Debt tracker 💳 · Pro",            body:"Pro feature — track all debts with halal payoff strategies. The app flags riba and shows exactly when you'll be debt-free.",                                                                      anchor:null,                    arrowDir:null   },
  { id:"zakat",      tab:"tools",  toolsTab:"zakat",   title:"Zakat calculator 🤲",              body:"Savings, investments and debts are pulled in automatically. It tells you if you've passed Nisab and exactly how much Zakat you owe.",                                                             anchor:null,                    arrowDir:null   },
  { id:"ai",         tab:"overview",                   title:"AI Finance Coach 🤖 · Pro",        body:"Pro feature — tap ✨ Ask AI to get personalised halal advice powered by Claude. Spending analysis, debt plans, Zakat guidance and more.",                                                                         anchor:"tour-ai",               arrowDir:"down" },
  { id:"months",     tab:"overview",                   title:"Browse past months 📆",            body:"Tap ‹ or › to travel back through your financial history. Every month has its own spending, income and category breakdown.",                                                                        anchor:"tour-month-nav",        arrowDir:"down" },
  { id:"export",      tab:"overview",                   title:"Export & backup 📤 · Pro",           body:"Pro feature — tap the ··· menu to export a full backup as JSON, or restore from another device. Your data is always yours.",                                                                                anchor:"tour-header-right",     arrowDir:"down" },
  { id:"done",       tab:"overview",                   title:"You're all set! ✨",                body:"Clear the demo and start with your own data. Everything stays private on your device — no accounts, no servers, no ads.",                                                                         anchor:null,                    arrowDir:null   },
];

export function Tour({ onEnd, setTab, setToolsTab }) {
  const [step, setStep]       = useState(0);
  const [pos, setPos]         = useState(null);  // null = hidden until ready
  const boxRef                = useRef();
  const current               = STEPS[step];
  const isLast                = step === STEPS.length - 1;
  const isFirst               = step === 0;

  useEffect(() => {
    if (current.tab) setTab(current.tab);
    if (current.toolsTab) setToolsTab(current.toolsTab);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    setPos(null); // hide immediately on step change
    if (!current.anchor) {
      // No anchor — show centered after brief delay for tab switch
      const t = setTimeout(() => setPos("center"), 300);
      return () => clearTimeout(t);
    }
    // Has anchor — scroll into view then calculate position
    const t = setTimeout(() => {
      const el = document.getElementById(current.anchor);
      if (!el) { setPos("center"); return; }
      el.scrollIntoView({ behavior:"smooth", block:"center" });
      setTimeout(() => {
        const rect  = el.getBoundingClientRect();
        const vw    = window.innerWidth;
        const vh    = window.innerHeight;
        const boxW  = Math.min(vw - 40, 380);
        const boxH  = boxRef.current?.offsetHeight || 200;
        const left  = Math.max(20, Math.min(vw - boxW - 20, rect.left + rect.width / 2 - boxW / 2));
        const arrow = Math.max(12, Math.min(boxW - 24, rect.left + rect.width / 2 - left));
        const spaceAbove = rect.top - 12;
        const spaceBelow = vh - rect.bottom - 12;
        if (spaceAbove >= boxH + 20) {
          setPos({ top: rect.top - boxH - 12, left, arrow, dir: "down" });
        } else if (spaceBelow >= boxH + 20) {
          setPos({ top: rect.bottom + 12, left, arrow, dir: "up" });
        } else {
          setPos({ top: Math.max(16, (vh - boxH) / 2), left, arrow: null, dir: null });
        }
      }, 450);
    }, 100);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]); // current.anchor intentionally omitted — only re-run on step change

  const go = (d) => {
    setPos(null);
    setTimeout(() => setStep(s => s + d), 150);
  };

  const arrowStyle = (dir) => ({
    position:"absolute",
    [dir === "up" ? "top" : "bottom"]: -9,
    left: Math.max(12, Math.min((pos?.arrow || 60) - 8, 340)),
    width:16, height:16,
    background:C.surface,
    transform:"rotate(45deg)",
    border:`1px solid rgba(61,214,140,0.3)`,
    ...(dir === "up"   ? { borderRight:"none", borderBottom:"none" } : { borderLeft:"none", borderTop:"none" }),
    zIndex:2,
  });

  return (
    <>
      <div style={{ position:"fixed", inset:0, zIndex:1100, background:"rgba(0,0,0,0.55)", pointerEvents:"none" }} />

      <div ref={boxRef} style={{
        position:"fixed", zIndex:1102,
        opacity: pos ? 1 : 0, transition:"opacity 0.25s ease",
        pointerEvents: pos ? "all" : "none",
        width:"calc(100% - 40px)", maxWidth:380,
        ...(pos === "center" || !pos
          ? { top:"50%", left:"50%", transform:"translate(-50%,-50%)" }
          : { top: pos.top, left: pos.left, transform:"none" }
        ),
      }}>
        {pos && pos !== "center" && pos.dir === "up" && <div style={arrowStyle("up")} />}

        <div style={{ background:C.surface, border:`1px solid rgba(61,214,140,0.3)`, borderRadius:16, overflow:"hidden", boxShadow:"0 12px 48px rgba(0,0,0,0.7)" }}>
          <div style={{ height:3, background:C.surface2 }}>
            <div style={{ height:"100%", width:`${((step+1)/STEPS.length)*100}%`, background:`linear-gradient(90deg,${C.green},#22c55e)`, transition:"width 0.4s ease" }} />
          </div>
          <div style={{ padding:"16px 18px 14px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
              <div style={{ display:"flex", gap:3 }}>
                {STEPS.map((_,i) => (
                  <div key={i} style={{ width:i===step?14:4, height:4, borderRadius:99, background:i<=step?C.green:C.surface3, transition:"all 0.25s", opacity:i===step?1:i<step?0.5:0.25 }} />
                ))}
              </div>
              <button onClick={onEnd} style={{ background:"none", border:"none", color:C.text3, fontSize:11, cursor:"pointer", fontWeight:600, padding:0 }}>Skip ✕</button>
            </div>
            <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:6 }}>{current.title}</div>
            <div style={{ fontSize:12, color:C.text2, lineHeight:1.7, marginBottom:14 }}>{current.body}</div>
            <div style={{ display:"flex", gap:8 }}>
              {!isFirst && (
                <button onClick={() => go(-1)} style={{ background:C.surface2, border:`1px solid ${C.border}`, color:C.text2, borderRadius:9, padding:"9px 14px", fontSize:12, cursor:"pointer", fontWeight:600 }}>← Back</button>
              )}
              <button onClick={() => isLast ? onEnd() : go(1)} style={{ flex:1, background:`linear-gradient(135deg,${C.green},#22c55e)`, border:"none", borderRadius:9, color:"#0e0e13", fontSize:13, fontWeight:800, padding:"9px", cursor:"pointer" }}>
                {isLast ? "Start tracking →" : `Next  ${step+1}/${STEPS.length} →`}
              </button>
            </div>
          </div>
        </div>

        {pos && pos !== "center" && pos.dir === "down" && <div style={arrowStyle("down")} />}
      </div>
    </>
  );
}